// @ts-strict-ignore
/**
 * SpaNavManager manages navigation of reports as a single page application.
 * i.e user feels like new page was loaded, but in reality report was swapped without a page refresh.
 * NOTE: This currently only works for iron apps.
 */

import {NavigateFunction} from 'react-router';

import {viewedReportFlagManager} from 'common/report/queries/app-api/user-flags';
import {trackVisitedReport} from 'common/util/server-tracking';
import {getLocation} from 'common/util/url';
import {VisitedReportReason} from 'common/util/server-tracking';
import {clearAllToasts} from 'common/components/toast-area/toast-manager';
import {diffFromNow, getNow} from 'common/util/timing';

import {waitUntilUiReady} from '../perf-measure';
import {ReportName} from '../constants';
import {getReportName, IronAppInfo, isPathBasedApp, urlToIronAppInfo} from './index';
import {parseStateFromUrl, UrlParsedState} from './state-from-url';

export interface InitOptions {
  projectId: string;
  projectName: string;
  navigate?: NavigateFunction;
}

// TODO(global-sidenav) remove once GA
/**
 * @deprecated
 */
export class SpaNavManager {
  private declare initOptions: InitOptions;

  initialize(initOptions: InitOptions) {
    this.initOptions = initOptions;
  }

  getIronAppInfo(urlState: UrlParsedState): IronAppInfo {
    const appName = urlState?.appName;
    return urlToIronAppInfo[appName] || null;
  }

  isNavigable(nextUrlPath: string, curUrlPath): boolean {
    const curUrlState = parseStateFromUrl(curUrlPath);
    const nextUrlState = parseStateFromUrl(nextUrlPath);
    const curApp = this.getIronAppInfo(curUrlState);
    const nextApp = this.getIronAppInfo(nextUrlState);

    if (nextApp && isPathBasedApp(nextApp.appName)) {
      return true;
    }

    // If you try to go to the current app, the actual behavior is to refresh the page.
    // If `appName` variable is the same, we don't have to ask to user with a "confirm" dialog.
    return Boolean(
      curApp &&
        nextApp &&
        curApp.appName !== nextApp.appName &&
        curUrlState.projectId === nextUrlState.projectId &&
        curUrlState.workspaceId === nextUrlState.workspaceId,
    );
  }

  async navigate(newUrlPath: string, curUrlPath: string, ev: Event | null, keepReportContext = false) {
    const startTime = getNow();
    const curApp = this.getIronAppInfo(parseStateFromUrl(curUrlPath));
    const newApp = this.getIronAppInfo(parseStateFromUrl(newUrlPath));

    if (this.initOptions.projectId) {
      viewedReportFlagManager.increment(); // async, no need to wait
    }

    if (!curApp || !newApp || (curApp.appName === newApp.appName && !isPathBasedApp(newApp.appName))) {
      // nothing to do, let the app's built-in router handle the navigation or let the browser refresh the page
      return;
    }

    if (ev && ev.type === `click`) {
      // don't reload the page with new url, we'll do a spa navigate
      ev.preventDefault();
    }

    if (this.initOptions.navigate) {
      const {pathname, hash, search} = new URL(newUrlPath, getLocation().origin);
      this.initOptions.navigate(`${pathname}${search}${hash}`);
      // wait for report shell to load and track report loaded events
      await waitUntilUiReady();
      const elapsedMs = diffFromNow(startTime);

      incrementSpaNavCount();
      clearAllToasts();

      this._trackReportLoaded(getReportName(newApp.appName), elapsedMs, keepReportContext);
    }
  }

  /**
   * track 'Viewed Report' and 'Report Loaded' events with 'is_spa_nav' flag so we can pretend a pageload occurred
   */
  private _trackReportLoaded(report: ReportName, elapsedMs: number, keepReportContext: boolean) {
    const projectId = this.initOptions.projectId;
    const commonTrackProps = {
      project_id: projectId,
      is_spa_nav: true,
      tab: report,
      keptReportContext: keepReportContext,
    };

    mixpanel.track(`Viewed report`, commonTrackProps);

    trackVisitedReport({
      projectId,
      reportName: report,
      reason: VisitedReportReason.ReportVoltNavigate,
    });

    mixpanel.track(`Report Loaded`, {
      ...commonTrackProps,
      preloadedAssets: true,
      page_time_to_ui_ready_ms: elapsedMs,
    });

    mixpanel.infra_metrics.track(`report-loaded`, {
      ...commonTrackProps,
      distinct_id: projectId,
      preloadedAssets: true,
      page_time_to_ui_ready_ms: elapsedMs,
    });
  }
}

function incrementSpaNavCount() {
  window[`__spaNavCount`] = (window[`__spaNavCount`] ?? 0) + 1;
}

export function getSpaNavCount() {
  return window[`__spaNavCount`] ?? 0;
}

// window singleton
export const spaNavManager: SpaNavManager = (window[`__spaNavManager`] =
  window[`__spaNavManager`] || new SpaNavManager());
